import { useMemo, useCallback, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useWeb3React } from '@web3-react/core'
import { useAppDispatch } from '~/state/hooks'
import { useSelectedConnectionType } from '~/state/user/hooks'
import { updatePendingChainId } from '~/state/user/actions'
import {
  useSpotSwapTokens,
  useSpotSwapUserConfig,
  useTokenlonV6NetworkConfigs,
} from '~/state/dex/hooks'
import { updateSpotSwapPair } from '~/state/dex/actions'
import { useIsContractAddress, useUpdaterChainId } from '~/hooks'
import { isAddress } from 'viem'
import { compareString } from '~/utils/common'
import { switchChain } from '~/utils/switchChain'
import { getSpotSwapSupportedNetworks } from '~/utils/chain'
import {
  findTokenByAddress,
  getIsNativeToken,
  getIsWeth,
  constructTokenId,
} from '~/utils/token'
import { formatETHCrossChain } from '~/components/Exchange/helpers'
import { QueryParams } from '../types'

const useQueryParams = () => {
  const router = useRouter()
  const {
    chainId,
    toChainId,
    inputAddress,
    outputAddress,
  } = router.query as QueryParams

  return useMemo(
    () => ({
      routerChainId: chainId ? Number(chainId) : null,
      routerToChainId: toChainId ? Number(toChainId) : null,
      inputAddress,
      outputAddress,
    }),
    [router.query],
  )
}

export const useTokenPairInitByRouter = () => {
  const router = useRouter()
  const {
    routerChainId,
    routerToChainId,
    inputAddress,
    outputAddress,
  } = useQueryParams()

  const { account: isWalletConnected, connector } = useWeb3React()
  const chainId = useUpdaterChainId()
  const tokens = Object.values(useSpotSwapTokens(routerChainId))
  const toTokens = Object.values(useSpotSwapTokens(routerToChainId))
  const { recipient } = useSpotSwapUserConfig()
  const config = useTokenlonV6NetworkConfigs()
  const isContractAddress = useIsContractAddress(recipient, routerToChainId)
  const selectedConnectionType = useSelectedConnectionType()

  const [awaitConnected, setAwaitConnected] = useState(false)
  const [awaitSetTokenPair, setAwaitSetTokenPair] = useState(false)

  const dispatch = useAppDispatch()

  const isCrossSwap = routerToChainId && routerToChainId !== routerChainId

  const isValidTokenAddresses = useMemo(() => {
    const isAddressValid = isAddress(inputAddress) && isAddress(outputAddress)
    const isSameChain = routerToChainId
      ? routerToChainId === routerChainId
      : true
    const isSameAddress = compareString(inputAddress, outputAddress)
    return isAddressValid && (isSameChain ? !isSameAddress : true)
  }, [inputAddress, outputAddress, routerChainId, routerToChainId])

  const supportedNetworks = getSpotSwapSupportedNetworks(config)

  const handleChainSwitch = useCallback(
    async (expectedChainId: number) => {
      // 如果 selectedConnectionType 有值代表上一次连接过，会触发 useEagerlyConnect 连接，等钱包连接后再触发 switchChain
      if (selectedConnectionType) {
        setAwaitConnected(true)
      } else {
        dispatch(updatePendingChainId(expectedChainId))
      }
      setAwaitSetTokenPair(isValidTokenAddresses)
    },
    [selectedConnectionType, isValidTokenAddresses],
  )

  const setTokenPair = useCallback(() => {
    if (isCrossSwap) {
      const newInputToken = findTokenByAddress(tokens, inputAddress)

      if (!newInputToken) return

      const isNativeOrWeth =
        getIsNativeToken(inputAddress) || getIsWeth(inputAddress, routerChainId)
      const crossChain = isNativeOrWeth
        ? formatETHCrossChain(newInputToken?.crossChain, isContractAddress)
        : newInputToken?.crossChain

      const newOutputTokenId = constructTokenId(routerToChainId, outputAddress)
      const isValidCrossToken = crossChain?.some(
        ({ destinationChainId, destinationToken }) => {
          const tokenId = constructTokenId(destinationChainId, destinationToken)
          return tokenId === newOutputTokenId
        },
      )

      if (!isValidCrossToken) return

      const newOutputToken = findTokenByAddress(toTokens, outputAddress)
      if (newOutputToken) {
        dispatch(
          updateSpotSwapPair({
            inputToken: newInputToken,
            outputToken: newOutputToken,
          }),
        )
      }
    } else {
      const [newInputToken, newOutputToken] = [
        inputAddress,
        outputAddress,
      ].map((addr) => findTokenByAddress(tokens, addr))

      if (newInputToken && newOutputToken) {
        dispatch(
          updateSpotSwapPair({
            inputToken: newInputToken,
            outputToken: newOutputToken,
          }),
        )
      }
    }
  }, [
    tokens?.length,
    toTokens?.length,
    routerChainId,
    routerToChainId,
    inputAddress,
    outputAddress,
    isCrossSwap,
    isContractAddress,
  ])

  useEffect(() => {
    if (!router.isReady || !routerChainId) return

    const isSupportedChain =
      supportedNetworks.includes(routerChainId) &&
      (routerToChainId ? supportedNetworks.includes(routerToChainId) : true)

    if (!isSupportedChain) return

    if (routerChainId !== chainId) {
      handleChainSwitch(routerChainId)
    } else if (
      isValidTokenAddresses &&
      (!isCrossSwap || (isCrossSwap && toTokens))
    ) {
      setTokenPair()
    }
  }, [
    router.isReady,
    routerChainId,
    routerToChainId,
    isCrossSwap,
    isValidTokenAddresses,
    toTokens?.length,
  ])

  useEffect(() => {
    const handleWalletConnected = async () => {
      if (awaitConnected && isWalletConnected) {
        setAwaitConnected(false)
        await switchChain(connector, routerChainId!)
        dispatch(updatePendingChainId(routerChainId!))
      }
    }
    handleWalletConnected()
  }, [isWalletConnected, awaitConnected])

  useEffect(() => {
    if (
      routerChainId === chainId &&
      awaitSetTokenPair &&
      !awaitConnected &&
      isValidTokenAddresses &&
      (!isCrossSwap || (isCrossSwap && toTokens?.length))
    ) {
      setAwaitSetTokenPair(false)
      setTokenPair()
    }
  }, [
    chainId,
    awaitConnected,
    awaitSetTokenPair,
    isCrossSwap,
    isValidTokenAddresses,
    toTokens?.length,
  ])
}
